import React, { useState, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, Card } from '@gu-corp/gu-ui';
import DepositItem from './deposit-item';
import { LendingPlanEnum } from '~/enums/lending-plan.enum';
import { useLendingPlanData } from '~/hooks/with-lending-data';

const LIMIT_RECORDS_PER_PAGE = 100;

const LendingHistory = () => {
  const { t } = useTranslation('common');
  const { data: seedData, error: seedError } = useLendingPlanData(LendingPlanEnum.SEED);
  const { data: sproutData, error: sproutError } = useLendingPlanData(LendingPlanEnum.SPROUT);
  const { data: harvestData, error: harvestError } = useLendingPlanData(LendingPlanEnum.HARVEST);

  const hasError = seedError || sproutError || harvestError;

  const records = useMemo(() => {
    return [
      ...(seedData?.records || []),
      ...(sproutData?.records || []),
      ...(harvestData?.records || []),
    ].sort((a, b) => b.createdAt - a.createdAt);
  }, [harvestData, seedData, sproutData]);

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(records.length / LIMIT_RECORDS_PER_PAGE);

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * LIMIT_RECORDS_PER_PAGE;
    return records.slice(startIndex, startIndex + LIMIT_RECORDS_PER_PAGE);
  }, [records, currentPage]);

  if (hasError) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="p-8 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <img src="/images/gu-logo.svg" alt="" className="w-8 h-8 opacity-50" />
          </div>
          <h3
            className="text-lg font-medium text-foreground mb-2"
            data-testid="lending-history-error-title"
          >
            {t('lending_page.lending_history_error_title')}
          </h3>
          <p className="text-sm text-muted-foreground" data-testid="lending-history-error-message">
            {t('lending_page.lending_history_error_message')}
          </p>
        </Card>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Card className="p-8 text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <img src="/images/gu-logo.svg" alt="" className="w-8 h-8 opacity-50" />
          </div>
          <h3
            className="text-lg font-medium text-foreground mb-2"
            data-testid="lending-history-empty-title"
          >
            {t('lending_page.lending_history_empty_title')}
          </h3>
          <p className="text-sm text-muted-foreground" data-testid="lending-history-empty-message">
            {t('lending_page.lending_history_empty_message')}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {paginatedRecords.map((item) => (
            <DepositItem key={item.id} record={item} />
          ))}
        </div>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button
            onClick={() => setCurrentPage((prev) => prev - 1)}
            disabled={currentPage === 1}
            variant="outline"
            data-testid="lending-history-previous-button"
          >
            {t('lending_page.previous')}
          </Button>

          <span className="text-sm text-muted-foreground" data-testid="lending-history-page-info">
            {t('lending_page.page')} {currentPage} {t('lending_page.of')} {totalPages}
          </span>

          <Button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={currentPage === totalPages}
            variant="outline"
            data-testid="lending-history-next-button"
          >
            {t('lending_page.next')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LendingHistory;
